import React, { FC, useEffect, useRef, useState } from "react"
import { Input, InputProps } from "antd"
import moment from "moment"

const ISO_DATE = "YYYY-MM-DD"
// Getippte Formate; YYYY-MM-DD zusätzlich, damit Tests und Autofill weiter ISO-Werte liefern können
const TYPED_FORMATS = ["DD.MM.YYYY", "D.M.YYYY", "DDMMYYYY", ISO_DATE]

/** Getipptes Datum → "YYYY-MM-DD", sonst undefined (auch bei unfertiger Eingabe wie "12.05.19"). */
export const parseTypedDate = (text: string): string | undefined => {
    const trimmed = text.trim()
    if (!/\d{4}/.test(trimmed)) return undefined
    const parsed = moment(trimmed, TYPED_FORMATS, true)
    return parsed.isValid() ? parsed.format(ISO_DATE) : undefined
}

const toDisplay = (isoDate?: string) => (isoDate ? moment(isoDate, ISO_DATE).format("DD.MM.YYYY") : "")

type BirthdayInputProps = Omit<InputProps, "value" | "onChange" | "type"> & {
    /** "YYYY-MM-DD" oder leer */
    value?: string
    /** Liefert "YYYY-MM-DD" bei vollständigem Datum, "" bei geleertem oder ungültigem Feld (beim Verlassen) */
    onChange?: (e: { target: { value: string } }) => void
}

/**
 * Geburtsdatum als getipptes Textfeld (TT.MM.JJJJ) statt nativem <input type="date">.
 * Das native Feld verliert auf Android-Chrome halb eingetippte Segmente, sobald das
 * value-Attribut neu geschrieben wird (Schildkrötli, 21.10.2026). Hier tippt der Nutzer
 * frei; nach außen geht nur ein vollständiges Datum oder "".
 */
const BirthdayInput: FC<BirthdayInputProps> = ({ value, onChange, onBlur, placeholder, ...rest }) => {
    const [text, setText] = useState(toDisplay(value))
    const lastEmitted = useRef(value ?? "")

    // Wert von außen (Laden, Zurücksetzen) übernehmen, eigene Emissionen nicht zurückspiegeln
    useEffect(() => {
        const next = value ?? ""
        if (next !== lastEmitted.current) {
            lastEmitted.current = next
            setText(toDisplay(next))
        }
    }, [value])

    const emit = (isoDate: string) => {
        if (isoDate === lastEmitted.current) return
        lastEmitted.current = isoDate
        onChange?.({ target: { value: isoDate } })
    }

    return (
        <Input
            {...rest}
            inputMode='numeric'
            autoComplete='bday'
            maxLength={10}
            placeholder={placeholder ?? "TT.MM.JJJJ"}
            value={text}
            onChange={(e) => {
                const next = e.target.value
                setText(next)
                if (next.trim() === "") {
                    emit("")
                    return
                }
                const parsed = parseTypedDate(next)
                if (parsed) emit(parsed)
            }}
            onBlur={(e) => {
                const parsed = parseTypedDate(text)
                if (parsed) {
                    setText(toDisplay(parsed))
                    emit(parsed)
                } else {
                    // Immer melden, auch wenn außen schon "" steht: der Aufrufer markiert das Feld
                    // als berührt und zeigt den Fehler zum stehen gebliebenen Text
                    lastEmitted.current = ""
                    onChange?.({ target: { value: "" } })
                }
                onBlur?.(e)
            }}
        />
    )
}

export default BirthdayInput
